import { useState } from 'react';
import { applyImport, planImport } from '../../app/backupImport';
import type { ImportPlan } from '../../app/backupImport';
import { exportBackup } from '../../app/backupExport';
import { errorMessage } from '../../app/errors';
import { log } from '../../app/logger';
import { updateSetting } from '../../app/settingsB';
import { config } from '../../config';
import { LOCK_NUDGE_CHOICES_B } from '../lockNudge';
import { THEME_CHOICES_B } from '../theme';
import type { ThemeChoiceB } from '../theme';
import { CredentialCardB } from './CredentialCardB';
import { ImportPlanSheet } from './ImportPlanSheet';
import { byteSizeB } from './moreB';
import { useMore } from './useMore';

/**
 * B spec §2.4 — **the More tab** (build unit B-19): export archive, import archive, the Coach
 * Claude service credential with its pre-flight chip, theme, and about/version.
 *
 * This is the tab that finishes three things the earlier units deliberately left open:
 *
 * | Deferred by | What lands here |
 * |---|---|
 * | B-15 / B-17 | the **service credential field** — without it Coach Claude cannot run at all |
 * | B-15 / B-17 | the **pre-flight chip**, the only reader of `GET /v1/coach/health` (§2.6) |
 * | B-4 / B-5 | the **theme control**, over CSS that has carried the selectors since B-0 |
 *
 * and it is the **first UI consumer anywhere in this repo** of `exportBackup` / `planImport` —
 * tested engine paths (`backupExport.test.ts`, `backupImport.test.ts`, `backupRoundTrip.test.ts`)
 * that A never wired to a screen.
 *
 * ── Reading the file is the only place this screen leaves the facades ────────
 *
 * `planImport` takes bytes; a `<input type="file">` yields a `File`. `file.arrayBuffer()` is the
 * platform's way across that gap and it happens here, in three lines, with the result handed
 * straight to the facade. No parsing, no inspection, no `src/archive/*` import — `readArchive`
 * still owns the container, and this screen never learns what is in it beyond the plan it is
 * handed back.
 *
 * ── Why "Restart Touchline" is offered after an import, and not run ──────────
 *
 * An import is **replace-all**: it wipes `activeMatch` and `uiSession` along with everything else.
 * `AppB` resolved where to resume once, at mount (§2.2), so after an import that resolve
 * describes a database that no longer exists — an archived match in progress would not be
 * offered until the next launch. A forced reload would also throw away the outcome report the
 * user is entitled to read. So the report renders, and the restart sits under it as a button:
 * the manual override path, taken by a finger, with the reason stated.
 *
 * ── "Lock nudge minutes" — flagged by B-19, ruled, and built in B-19a ────────
 *
 * §2.4 lists it in this tab, and B-19 declined to build it: `domain/chrome.lockNudgeDue` took no
 * threshold, so a control here would have written a setting that changed nothing a user could
 * ever observe — the exact defect a settings screen must never contain. Making it live meant
 * changing a working engine predicate (reuse contract clause 5 — flag, never change), so it was
 * flagged. Andrew ruled: *"I'm fine with the engine extension for this specific purpose."* The
 * predicate now takes an optional threshold defaulting to the constant **by reference** (A is
 * unchanged), `ui-b/lockNudge.ts` parses the stored value, and the takeover chrome evaluates the
 * nudge against it. What this screen shows is that parse's answer, not the raw row — see
 * `useMore`.
 */

const MODULE = 'ui-b/more';

type ExportReport = {
  readonly filename: string;
  readonly bytes: number;
  readonly blobs: number;
  readonly shared: boolean;
};

type ImportReport = { readonly written: number; readonly removed: number };

type ImportStage =
  | { readonly kind: 'idle' }
  | { readonly kind: 'reading' }
  | { readonly kind: 'planned'; readonly plan: ImportPlan; readonly filename: string }
  | { readonly kind: 'done'; readonly report: ImportReport };

const THEME_LABELS: Readonly<Record<ThemeChoiceB, string>> = { light: 'Light', dark: 'Dark' };

/**
 * The message a **user** should read, without the error class name.
 *
 * `errorMessage` prefixes `err.name` — right for the OBS-1 ring, wrong on a screen: it produced
 * *"ArchiveError: This file has no zip end-of-central-directory record."* on the import card,
 * which opens a sentence to a coach with a class name (found by reading this unit's screenshots).
 * The log keeps the prefixed form; the screen gets the sentence.
 */
function sentence(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

export function MoreScreen() {
  const { state, actions } = useMore();
  const [busy, setBusy] = useState(false);
  const [exported, setExported] = useState<ExportReport | null>(null);
  const [exportProblem, setExportProblem] = useState<string | null>(null);
  const [stage, setStage] = useState<ImportStage>({ kind: 'idle' });
  const [importProblem, setImportProblem] = useState<string | null>(null);
  const [themeProblem, setThemeProblem] = useState<string | null>(null);
  const [nudgeProblem, setNudgeProblem] = useState<string | null>(null);

  const runExport = (): void => {
    setBusy(true);
    setExportProblem(null);
    setExported(null);
    void exportBackup(Date.now())
      .then((done) => {
        setExported({
          filename: done.filename,
          bytes: done.result.bytes.length,
          blobs: done.result.blobCount,
          shared: done.route === 'share-file',
        });
      })
      .catch((cause: unknown) => {
        // A cancelled share sheet throws `ExportError` with a sentence already written for a
        // user (`app/shareFile`), so it is surfaced verbatim rather than re-worded here.
        log.warn(MODULE, 'B §2.4: the archive was not saved', { message: errorMessage(cause) });
        setExportProblem(sentence(cause));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const pickFile = (file: File | undefined): void => {
    if (file === undefined) return;
    setStage({ kind: 'reading' });
    setImportProblem(null);
    void file
      .arrayBuffer()
      .then((buffer) => planImport([{ bytes: new Uint8Array(buffer), label: file.name }]))
      .then((plan) => {
        setStage({ kind: 'planned', plan, filename: file.name });
      })
      .catch((cause: unknown) => {
        // `readArchive` throws for an unreadable container or a format version this bundle cannot
        // read (SYN-9's one-directional gate — how a B export announces itself to an older app).
        log.warn(MODULE, 'B §2.4: a picked file could not be read as a Touchline archive', {
          message: errorMessage(cause),
        });
        setStage({ kind: 'idle' });
        setImportProblem(`That file could not be read as a Touchline archive. ${sentence(cause)}`);
      });
  };

  const confirmImport = (plan: ImportPlan): void => {
    setBusy(true);
    setImportProblem(null);
    void applyImport(plan)
      .then((outcome) => {
        setStage({
          kind: 'done',
          report: { written: outcome.written.total, removed: plan.willRemove.total },
        });
        actions.refresh();
      })
      .catch((cause: unknown) => {
        // The transaction aborts as a whole: the profile is either fully replaced or untouched.
        log.error(MODULE, 'B §2.4: the import failed and nothing was changed', {
          message: errorMessage(cause),
        });
        setImportProblem(`${sentence(cause)} Nothing on this device was changed.`);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const chooseTheme = (choice: ThemeChoiceB): void => {
    setBusy(true);
    setThemeProblem(null);
    void updateSetting('themePreference', choice)
      .then((result) => {
        if (!result.ok) {
          setThemeProblem(result.message);
          return;
        }
        // The re-read is what recolours the document (`useMore` applies what it reads), so a
        // refused write leaves both the screen and the page exactly as they were.
        actions.refresh();
      })
      .catch((cause: unknown) => {
        log.error(MODULE, 'B §2.3: the theme could not be saved', { message: errorMessage(cause) });
        setThemeProblem('That could not be saved. The log has the detail.');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  /**
   * B-19a — LOCK-1's configurable minute (§2.4). The same write-then-re-read shape as the theme:
   * the value the screen renders is always what `useMore` read back through
   * `lockNudgeMinFromB`, never this component's belief about its own write, so a refused write
   * leaves the control exactly where it was.
   */
  const chooseLockNudge = (minute: number): void => {
    setBusy(true);
    setNudgeProblem(null);
    void updateSetting('lockNudgeMin', minute)
      .then((result) => {
        if (!result.ok) {
          setNudgeProblem(result.message);
          return;
        }
        actions.refresh();
      })
      .catch((cause: unknown) => {
        log.error(MODULE, 'B §2.4: the lock nudge minute could not be saved', {
          message: errorMessage(cause),
        });
        setNudgeProblem('That could not be saved. The log has the detail.');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  if (state.phase === 'loading') return <p className="homeb-empty">Reading your settings…</p>;
  if (state.phase === 'failed')
    return (
      <p className="homeb-empty" role="alert">
        Your settings could not be read. The log has the detail.
      </p>
    );

  return (
    <div
      className="more-b"
      data-testid="more-b"
      data-read={String(state.reads)}
      data-credential={state.credential.stored ? 'stored' : 'none'}
      data-theme-choice={state.theme ?? 'system'}
      /* The EFFECTIVE minute — the parse's answer, which is the number the nudge uses. A pass
         reading this attribute is reading the same fact the capture chrome reads. */
      data-lock-nudge={String(state.lockNudgeMin)}
    >
      <h1 className="more-b__title">More</h1>

      {/* ── Export (§2.4; scenario 35) ──────────────────────────────────────── */}
      <section className="more-card" data-testid="more-export-card" aria-labelledby="more-exp-h">
        <h2 className="more-card__title" id="more-exp-h">
          Export an archive
        </h2>
        <p className="more-card__why">
          One file holding everything on this device — every match, pattern, flag, voice note and
          card. Keep it off the phone: it is the copy that survives a lost phone, and the way to
          move this data to another device.
        </p>
        <div className="more-actions">
          <button
            type="button"
            className="more-btn more-btn--primary"
            data-testid="more-export"
            disabled={busy}
            onClick={runExport}
          >
            Export everything
          </button>
        </div>
        {exported !== null && (
          <p className="more-notice" data-testid="more-export-result" role="status">
            {exported.shared ? 'Shared' : 'Saved'} <strong>{exported.filename}</strong> ·{' '}
            {byteSizeB(exported.bytes)} · {exported.blobs} voice note
            {exported.blobs === 1 ? '' : 's'} included.
          </p>
        )}
        {exportProblem !== null && (
          <p className="more-problem" data-testid="more-export-problem" role="status">
            {exportProblem}
          </p>
        )}
      </section>

      {/* ── Import (§2.4; scenario 35 — the plan IS the confirm step) ───────── */}
      <section className="more-card" data-testid="more-import-card" aria-labelledby="more-imp-h">
        <h2 className="more-card__title" id="more-imp-h">
          Import an archive
        </h2>
        <p className="more-card__why">
          Reads a Touchline archive — including one exported by the older app — and{' '}
          <strong>replaces everything on this device</strong> with it. You will see exactly what
          goes and what arrives before anything is touched.
        </p>
        <label className="more-field">
          <span className="more-field__label">Choose an archive file</span>
          <input
            className="more-field__file"
            data-testid="more-import-file"
            type="file"
            accept=".zip,application/zip"
            disabled={busy || stage.kind === 'reading'}
            onChange={(event) => {
              pickFile(event.target.files?.[0]);
              // Cleared so picking the SAME file again still fires a change event.
              event.target.value = '';
            }}
          />
        </label>
        {stage.kind === 'reading' && (
          <p className="more-notice" role="status">
            Reading the file…
          </p>
        )}
        {stage.kind === 'done' && (
          <div className="more-notice" data-testid="more-import-result" role="status">
            <p>
              Imported <strong>{stage.report.written}</strong> rows, replacing{' '}
              {stage.report.removed} that were on this device.
            </p>
            <p className="more-card__note">
              Restart Touchline so every screen reads the imported data from the start.
            </p>
            <button
              type="button"
              className="more-btn more-btn--primary"
              data-testid="more-import-reload"
              onClick={() => {
                window.location.reload();
              }}
            >
              Restart Touchline
            </button>
          </div>
        )}
        {importProblem !== null && stage.kind !== 'planned' && (
          <p className="more-problem" data-testid="more-import-problem-inline" role="status">
            {importProblem}
          </p>
        )}
      </section>

      {/* ── Coach Claude: the credential, and §2.6's pre-flight ─────────────── */}
      <CredentialCardB credential={state.credential} onWritten={actions.refresh} />

      {/* ── Theme (§2.3) ───────────────────────────────────────────────────── */}
      <section className="more-card" data-testid="more-theme-card" aria-labelledby="more-thm-h">
        <h2 className="more-card__title" id="more-thm-h">
          Theme
        </h2>
        <div className="more-choices" role="radiogroup" aria-labelledby="more-thm-h">
          {THEME_CHOICES_B.map((choice) => (
            <button
              key={choice}
              type="button"
              className="more-choice"
              data-testid={`more-theme-${choice}`}
              role="radio"
              aria-checked={state.theme === choice}
              disabled={busy}
              onClick={() => {
                chooseTheme(choice);
              }}
            >
              {THEME_LABELS[choice]}
            </button>
          ))}
        </div>
        <p className="more-card__note" data-testid="more-theme-note">
          {state.theme === null
            ? 'Following this device’s own setting. Light is what a pitch in daylight needs — a phone at full brightness is most readable when the screen is white.'
            : 'Light is what a pitch in daylight needs — a phone at full brightness is most readable when the screen is white. Dark is here for indoors and evenings.'}
        </p>
        {themeProblem !== null && (
          <p className="more-problem" data-testid="more-theme-problem" role="status">
            {themeProblem}
          </p>
        )}
      </section>

      {/* ── Lock nudge minutes (§2.4; LOCK-1, ruled extension B-19a) ───────── */}
      <section className="more-card" data-testid="more-nudge-card" aria-labelledby="more-ndg-h">
        <h2 className="more-card__title" id="more-ndg-h">
          Lock nudge
        </h2>
        <p className="more-card__why">
          How far into a half before Touchline offers to lock your board. It is only ever an
          offer — the app never locks a board for you.
        </p>
        <div className="more-choices" role="radiogroup" aria-labelledby="more-ndg-h">
          {LOCK_NUDGE_CHOICES_B.map((minute) => (
            <button
              key={minute}
              type="button"
              className="more-choice more-choice--minute"
              data-testid={`more-nudge-${String(minute)}`}
              role="radio"
              aria-checked={state.lockNudgeMin === minute}
              aria-label={`${String(minute)} minutes`}
              disabled={busy}
              onClick={() => {
                chooseLockNudge(minute);
              }}
            >
              {minute} min
            </button>
          ))}
        </div>
        <p className="more-card__note" data-testid="more-nudge-note">
          Offered from <strong>{state.lockNudgeMin} minutes</strong>, and again at the break if you
          are still in Discovery.
        </p>
        {nudgeProblem !== null && (
          <p className="more-problem" data-testid="more-nudge-problem" role="status">
            {nudgeProblem}
          </p>
        )}
      </section>

      {/* ── About ──────────────────────────────────────────────────────────── */}
      <section className="more-card" data-testid="more-about" aria-labelledby="more-abt-h">
        <h2 className="more-card__title" id="more-abt-h">
          About
        </h2>
        <dl className="more-about">
          <dt>Build</dt>
          <dd data-testid="more-build-id">{__BUILD_ID__}</dd>
          <dt>Archive format</dt>
          <dd data-testid="more-archive-format">{config.ARCHIVE_FORMAT_VERSION}</dd>
          <dt>Database</dt>
          <dd data-testid="more-db-version">v{config.PRODUCT_DB_VERSION}</dd>
        </dl>
        <p className="more-card__note">
          Touchline keeps everything on this device. Nothing is uploaded anywhere unless you export
          it yourself, and the only network call the app ever makes is Coach Claude.
        </p>
      </section>

      {stage.kind === 'planned' && (
        <ImportPlanSheet
          plan={stage.plan}
          filename={stage.filename}
          deviceLocal={{
            credentialStored: state.credential.stored,
            themeStored: state.theme !== null,
          }}
          profileNow={{
            // §15.6 — read back through `useMore`, like everything else this screen renders.
            shapeStored: state.shapeStored,
            rosterCount: state.rosterCount,
          }}
          busy={busy}
          problem={importProblem}
          onConfirm={() => {
            confirmImport(stage.plan);
          }}
          onCancel={() => {
            setStage({ kind: 'idle' });
            setImportProblem(null);
          }}
        />
      )}
    </div>
  );
}
